import React from 'react'
import { Moon, Sun, Monitor, Check } from 'lucide-react'
import { useTheme } from '@/hooks/useTheme'
import { ThemeMode } from '@/store/slices/themeSlice'

interface ThemeToggleProps {
  variant?: 'button' | 'dropdown'
  className?: string
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  variant = 'button', 
  className = '' 
}) => {
  const { mode, toggle, setMode, isDark } = useTheme()

  if (variant === 'button') {
    return (
      <button
        onClick={toggle}
        className={`
          p-2 rounded-lg border border-border bg-background hover:bg-accent
          transition-colors duration-200 flex items-center justify-center
          ${className}
        `}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
      </button>
    )
  }

  return (
    <div className={`relative group ${className}`}>
      <button
        className="p-2 rounded-lg border border-border bg-background hover:bg-accent transition-colors duration-200 flex items-center justify-center"
        aria-label="Theme options"
      >
        {isDark ? (
          <Moon className="h-4 w-4" />
        ) : (
          <Sun className="h-4 w-4" />
        )}
      </button>
      
      <div className="absolute right-0 mt-2 w-32 bg-popover border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        <div className="p-1">
          {[
            { mode: 'light' as ThemeMode, icon: Sun, label: 'Light' },
            { mode: 'dark' as ThemeMode, icon: Moon, label: 'Dark' },
            { mode: 'system' as ThemeMode, icon: Monitor, label: 'System' },
          ].map(({ mode: themeMode, icon: Icon, label }) => (
            <button
              key={themeMode}
              onClick={() => setMode(themeMode)}
              className={`
                w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm
                hover:bg-accent transition-colors duration-150
                ${mode === themeMode ? 'bg-accent' : ''}
              `}
            >
              <Icon className="h-3 w-3" />
              <span className="flex-1 text-left">{label}</span>
              {mode === themeMode && <Check className="h-3 w-3" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
} 